import React, {
  ReactElement,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import moment from 'moment';
import { Flatten } from '@erecruit/core';
import { Mutations } from '@erecruit/types';
import { Form } from '@erecruit/ui-kit';
import { WizardLocationState } from '@erecruit/core';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import RoutedWizard from '../../App/components/RoutedWizard';
import {
  generateFieldObjects,
  hasValidationErrors,
  getInitialValuesFromTemplate,
} from './helpers';
import {
  AddNewPositionWizardFields,
  WizardContext,
  PositionTemplate,
} from './types';
import { getWizardSteps } from './components/getWizardSteps';
import {
  selectors,
  savePosition,
  resetPosition,
  clearPositionTemplateDetails,
} from '../RequestPosition/state/reducer';
import { degenerateFolderGroupId } from './components/DynamicFormItems/helpers';
import { DEFAULT_SHIFT_FORMAT } from '../../constants';

interface AddNewPositionWizardLocationState extends WizardLocationState {
  positionTemplate: PositionTemplate;
}

interface AddNewPositionWizardProps {
  isLoading: boolean;
  positionId?: number;
  savePosition: typeof savePosition;
  selectedCompany: number;
  resetPosition: typeof resetPosition;
  clearPositionTemplateDetails: typeof clearPositionTemplateDetails;
}

const initialFormFieldsValues = {
  general: {
    positionTitle: '',
  },
  location: {
    facilityDepartments: [],
  },
  schedule: {},
  billing: {},
  additionalInfo: {},
  review: {},
};

const transformValues = <T extends Flatten<AddNewPositionWizardFields>>(
  values: T,
  positionTemplateId: number,
  customFields: Mutations.CustomField[],
): Mutations.NewPositionParams => {
  return {
    ...values,
    positionTemplateId,
    shiftTypeId: undefined,
    endTime:
      values.endTime && moment(values.endTime).format(DEFAULT_SHIFT_FORMAT),
    shiftTypes: values.shiftTypeId && [values.shiftTypeId],
    startTime:
      values.startTime && moment(values.startTime).format(DEFAULT_SHIFT_FORMAT),
    worksiteAddress: values.worksiteAddress,
    folderGroup:
      values.folderGroup && degenerateFolderGroupId(values.folderGroup),
    customFields,
  };
};

const AddNewPositionWizard = ({
  isLoading,
  positionId,
  savePosition,
  selectedCompany,
  clearPositionTemplateDetails,
  resetPosition,
}: AddNewPositionWizardProps): ReactElement => {
  const formRef = useRef<WrappedFormUtils>();
  const form = formRef.current;
  const history = useHistory();
  const location = useLocation();
  const [locationState] = useState<AddNewPositionWizardLocationState>(
    () => location && location.state,
  );
  const referrer = locationState ? locationState.referrer : undefined;
  const positionTemplate = locationState.positionTemplate
    ? locationState.positionTemplate
    : {
        id: -1,
        name: '',
        fields: [],
        customFields: [],
      };

  const [showWizard, setShowWizard] = useState(true);

  const [formFieldsValues, setFormFieldsValues] = useState<
    AddNewPositionWizardFields
  >(initialFormFieldsValues);

  useEffect(() => {
    if (positionTemplate && positionTemplate.id && positionTemplate.id !== -1) {
      setFormFieldsValues(getInitialValuesFromTemplate(positionTemplate));
    } else {
      history.push('/requestPosition', { modal: true, referrer });
    }
  }, [history, positionTemplate, referrer]);

  useEffect(() => {
    if (positionId) {
      // New position created
      // Clear last position request; prepare state for new
      resetPosition();
      history.push(`/positions/${positionId}`);
    }
  }, [history, positionId, resetPosition]);

  const wizardSteps = useMemo(
    () =>
      getWizardSteps(
        positionTemplate.id,
        positionTemplate?.fields || [],
        positionTemplate?.customFields || [],
      ),
    [positionTemplate],
  );

  const progress = wizardSteps.findIndex(
    step => step.route === location.pathname,
  );

  useEffect(() => {
    if (progress >= 0) {
      const stepKey = wizardSteps[progress]
        .name as keyof AddNewPositionWizardFields;
      const formFields = generateFieldObjects(formFieldsValues[stepKey]);
      form && form.setFields(formFields);
    }
  }, [form, formFieldsValues, progress, wizardSteps]);

  const handleBack = (pushPreviousStep: Function) => {
    if (progress === 0) {
      clearPositionTemplateDetails();
      history.push('/requestPosition', {
        modal: true,
        referrer,
        reset: true,
      });
    } else {
      pushPreviousStep();
    }
  };
  const handleNextStep = (nextStepCb: Function) => {
    form && form.validateFields();
    if (!hasValidationErrors(form && form.getFieldsError())) {
      // Store form fields before progressing
      setFormFieldsValues({
        ...formFieldsValues,
        [wizardSteps[progress].name]: (form && form.getFieldsValue()) || {},
      });
      nextStepCb();
    }
  };

  const handleSubmit = () => {
    form && form.validateFields();

    if (hasValidationErrors(form && form.getFieldsError())) {
      return;
    }

    const reviewStepValues = form && form.getFieldsValue();
    const { additionalInfo, ...otherSections } = formFieldsValues;

    const valuesWithoutSections: {} = Object.values(otherSections).reduce(
      (result: {}, values: { [value: string]: AddNewPositionWizardFields }) => {
        return { ...result, ...values };
      },
      {},
    );

    const customFields = Object.keys(additionalInfo).map(key => ({
      id: Number(key),
      value: JSON.stringify(additionalInfo[key]),
    }));

    const values = transformValues(
      {
        ...valuesWithoutSections,
        ...reviewStepValues,
        company: selectedCompany,
      },
      positionTemplate.id,
      customFields,
    );

    // remove wizard but leave component so it can
    // redirect when positionId is fetched from api
    setShowWizard(false);

    savePosition(values);
  };

  return (
    <Form wrappedComponentRef={formRef}>
      <WizardContext.Provider value={formFieldsValues}>
        {showWizard && (
          <RoutedWizard
            onExit={() => clearPositionTemplateDetails()}
            isLoading={isLoading}
            steps={wizardSteps}
            onBack={handleBack}
            onNextStep={handleNextStep}
            onSubmit={handleSubmit}
          />
        )}
      </WizardContext.Provider>
    </Form>
  );
};

export default connect(
  state => ({
    isLoading: selectors.selectRequestPositionState(state).isLoading,
    positionId: selectors.selectRequestPositionState(state).positionId,
    selectedCompany: selectors.selectRequestPositionState(state)
      .selectedCompany,
  }),
  {
    savePosition,
    resetPosition,
    clearPositionTemplateDetails,
  },
)(AddNewPositionWizard);
